import { useState, useEffect, axios, Button, Input, Card, CardContent, CardHeader, CardTitle, Badge, Label, Loader2, TrendingUp, Sparkles, Check, Copy, CalendarDays, API, BACKEND_URL, useAuth, toast } from "@/pages/shared";

const scheduleCampaignWeek = async (campaignId, onDone) => {
  const start = new Date();
  start.setDate(start.getDate() + 1);
  start.setHours(9, 0, 0, 0);
  try {
    const r = await axios.post(`${API}/campaigns/${campaignId}/schedule`, { start_at: start.toISOString() });
    if (r.data.scheduled === 0) toast.info(r.data.message || "Nothing left to schedule");
    else toast.success(`Scheduled ${r.data.scheduled} posts — one per day from ${new Date(r.data.first_at).toLocaleString()}`);
    onDone?.(r.data);
  } catch (e) {
    toast.error(e.response?.data?.detail || "Scheduling failed");
  }
};

const CHANNELS = [
  { value: "linkedin", label: "LinkedIn" },
  { value: "twitter", label: "X / Twitter" },
  { value: "instagram", label: "Instagram" },
  { value: "tiktok", label: "TikTok" },
];

const FunnelStat = ({ label, value, accent }) => (
  <div className="flex-1 min-w-[100px] p-3 rounded-lg bg-white/5 border border-white/10 text-center">
    <p className={`text-2xl font-bold ${accent || ""}`}>{value}</p>
    <p className="text-xs text-muted-foreground">{label}</p>
  </div>
);

const CampaignFunnel = ({ campaignId }) => {
  const [funnel, setFunnel] = useState(null);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [scheduling, setScheduling] = useState(false);

  const load = () => axios.get(`${API}/campaigns/${campaignId}/funnel`).then((r) => setFunnel(r.data)).catch(() => {});
  useEffect(() => { load(); }, [campaignId]); // eslint-disable-line react-hooks/exhaustive-deps

  const scheduleWeek = async () => {
    setScheduling(true);
    await scheduleCampaignWeek(campaignId, load);
    setScheduling(false);
  };

  const addOutcome = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/campaigns/${campaignId}/revenue`, { amount: amt, note });
      toast.success("Outcome recorded");
      setAmount(""); setNote("");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to record"); }
    setSaving(false);
  };

  if (!funnel) return <Loader2 className="w-4 h-4 animate-spin text-violet-500 my-3" />;
  return (
    <div className="mt-3 space-y-3" data-testid={`campaign-funnel-${campaignId}`}>
      <div className="flex gap-2 flex-wrap">
        <FunnelStat label="Posts" value={funnel.posts_total} />
        <FunnelStat label="Published" value={funnel.posts_published} />
        <FunnelStat label="Clicks" value={funnel.clicks} accent="text-teal-400" />
        <FunnelStat label="Leads" value={funnel.leads} accent="text-blue-400" />
        <FunnelStat label="Revenue" value={`£${funnel.revenue}`} accent="text-violet-400" />
      </div>
      {funnel.clicks_by_channel?.length > 0 && (
        <p className="text-xs text-muted-foreground">By channel: {funnel.clicks_by_channel.map((c) => `${c.channel}: ${c.clicks}`).join(" · ")}</p>
      )}
      {funnel.posts_published + 0 < funnel.posts_total && (
        <Button size="sm" variant="outline" onClick={scheduleWeek} disabled={scheduling} className="border-teal-500/40 text-teal-400 hover:bg-teal-500/10 h-8 text-xs" data-testid={`campaign-schedule-week-btn-${campaignId}`}>
          {scheduling ? <Loader2 className="w-3 h-3 animate-spin" /> : <><CalendarDays className="w-3 h-3 mr-1" />Auto-schedule week (1 post/day from tomorrow 9:00)</>}
        </Button>
      )}
      <form onSubmit={addOutcome} className="flex gap-2 items-center flex-wrap">
        <Input value={amount} onChange={(e) => setAmount(e.target.value)} type="number" step="0.01" min="0" placeholder="£ amount" className="w-28 bg-white/5 border-white/10 h-8 text-sm" data-testid={`campaign-revenue-amount-${campaignId}`} />
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. 1 coaching client from this campaign" className="flex-1 min-w-[180px] bg-white/5 border-white/10 h-8 text-sm" data-testid={`campaign-revenue-note-${campaignId}`} />
        <Button type="submit" size="sm" disabled={saving} className="bg-violet-600 hover:bg-violet-700 h-8 text-xs" data-testid={`campaign-add-outcome-btn-${campaignId}`}>
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Add outcome"}
        </Button>
      </form>
      <p className="text-[10px] text-muted-foreground">Revenue outcomes are manually recorded and labelled as manual attribution — Creatara never invents revenue figures.</p>
    </div>
  );
};

export default function CampaignsPage() {
  const { user } = useAuth();
  const [goals, setGoals] = useState({});
  const [selectedGoal, setSelectedGoal] = useState("");
  const [topic, setTopic] = useState("");
  const [audience, setAudience] = useState("");
  const [offer, setOffer] = useState("");
  const [cta, setCta] = useState("");
  const [destinationUrl, setDestinationUrl] = useState("");
  const [leadFormId, setLeadFormId] = useState("");
  const [leadForms, setLeadForms] = useState([]);
  const [channels, setChannels] = useState(["linkedin"]);
  const [generating, setGenerating] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [quota, setQuota] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/campaigns/goals`).then((r) => setGoals(r.data)).catch(() => {});
    axios.get(`${API}/campaigns`).then((r) => setCampaigns(r.data)).catch(() => {});
    axios.get(`${API}/campaigns/quota/me`).then((r) => setQuota(r.data)).catch(() => {});
    axios.get(`${API}/leads/forms`).then((r) => setLeadForms(r.data)).catch(() => {});
  }, []);

  const toggleChannel = (v) =>
    setChannels((prev) => (prev.includes(v) ? (prev.length > 1 ? prev.filter((c) => c !== v) : prev) : [...prev, v]));

  const generate = async () => {
    if (!selectedGoal) { toast.error("Pick a goal first"); return; }
    if (topic.trim().length < 5) { toast.error("Topic must be at least 5 characters"); return; }
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/campaigns/generate`, {
        goal: selectedGoal, topic: topic.trim(),
        audience: audience.trim() || null,
        offer: offer.trim() || null,
        cta: cta.trim() || null,
        destination_url: destinationUrl.trim() || null,
        lead_form_id: leadFormId || null,
        channels,
        target_platform: channels[0],
      });
      toast.success("Campaign generated!");
      setActiveCampaign(r.data);
      setTopic(""); setDestinationUrl(""); setAudience(""); setOffer(""); setCta(""); setLeadFormId("");
      axios.get(`${API}/campaigns`).then((res) => setCampaigns(res.data)).catch(() => {});
      axios.get(`${API}/campaigns/quota/me`).then((res) => setQuota(res.data)).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Campaign generation failed");
    }
    setGenerating(false);
  };

  const upgradeRequired = quota && quota.limit === 0;
  const canGenerate = !quota || quota.remaining > 0;

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      <div>
        <h1 className="text-3xl font-bold mb-2 flex items-center gap-2"><TrendingUp className="w-8 h-8 text-violet-500" /> Revenue Campaigns</h1>
        <p className="text-muted-foreground">What do you want to promote this week? One objective in — a coordinated, tracked campaign out.</p>
      </div>

      {upgradeRequired && (
        <Card className="glass border-amber-500/30">
          <CardContent className="p-6">
            <Badge className="mb-3 bg-amber-600">Pro or Enterprise required</Badge>
            <p className="text-sm text-muted-foreground">Revenue Campaigns are available on the Pro (£29/mo, 5 campaigns) and Enterprise (£99/mo, 30 campaigns) plans. Each campaign generates a coordinated multi-angle content sequence + a tracked link — then reports the funnel from posts to clicks, leads and revenue.</p>
          </CardContent>
        </Card>
      )}

      {quota && quota.limit > 0 && (
        <Card className="glass">
          <CardContent className="p-4 flex items-baseline justify-between">
            <p className="text-sm text-muted-foreground">This month</p>
            <p className="text-lg font-semibold">{quota.used} <span className="text-muted-foreground text-sm font-normal">/ {quota.limit} campaigns</span></p>
          </CardContent>
        </Card>
      )}

      <Card className="glass">
        <CardHeader><CardTitle>What do you want to promote this week?</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="campaigns-goals-grid">
            {Object.entries(goals).map(([key, g]) => (
              <button
                key={key}
                onClick={() => canGenerate && setSelectedGoal(key)}
                disabled={!canGenerate}
                className={`text-left p-4 rounded-xl border transition-all ${selectedGoal === key ? "border-violet-500 bg-violet-600/10" : "border-white/10 hover:border-white/20 bg-white/5"} ${!canGenerate ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                data-testid={`campaigns-goal-${key}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <p className="font-semibold">{g.label}</p>
                  {selectedGoal === key && <Check className="w-5 h-5 text-violet-500 flex-shrink-0" />}
                </div>
                <p className="text-xs text-muted-foreground mb-2">{g.description}</p>
                <p className="text-xs text-violet-400">{g.outputs.join(" · ")}</p>
              </button>
            ))}
          </div>

          <div>
            <Label>Topic / theme *</Label>
            <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. Sustainable cookware for tiny kitchens" className="mt-1 bg-white/5 border-white/10" disabled={!canGenerate} data-testid="campaigns-topic-input" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Audience / ideal customer <span className="text-muted-foreground">(optional)</span></Label>
              <Input value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="e.g. first-time founders drowning in admin" className="mt-1 bg-white/5 border-white/10" disabled={!canGenerate} data-testid="campaigns-audience-input" />
            </div>
            <div>
              <Label>Offer being promoted <span className="text-muted-foreground">(optional)</span></Label>
              <Input value={offer} onChange={(e) => setOffer(e.target.value)} placeholder="e.g. 1:1 strategy call, £150" className="mt-1 bg-white/5 border-white/10" disabled={!canGenerate} data-testid="campaigns-offer-input" />
            </div>
            <div>
              <Label>Call-to-action <span className="text-muted-foreground">(optional)</span></Label>
              <Input value={cta} onChange={(e) => setCta(e.target.value)} placeholder="e.g. Book your free audit" className="mt-1 bg-white/5 border-white/10" disabled={!canGenerate} data-testid="campaigns-cta-input" />
            </div>
            <div>
              <Label>Destination URL <span className="text-muted-foreground">(optional — gets a tracked link)</span></Label>
              <Input value={destinationUrl} onChange={(e) => { setDestinationUrl(e.target.value); if (e.target.value) setLeadFormId(""); }} placeholder="https://your-offer-page.com" className="mt-1 bg-white/5 border-white/10" disabled={!canGenerate} data-testid="campaigns-destination-input" />
            </div>
          </div>

          {leadForms.length > 0 && (
            <div>
              <Label>…or send traffic to one of your lead forms</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {leadForms.slice(0, 6).map((f) => (
                  <button key={f.id} onClick={() => { if (!canGenerate) return; setLeadFormId(leadFormId === f.id ? "" : f.id); if (leadFormId !== f.id) setDestinationUrl(""); }}
                    className={`px-3 py-1.5 rounded-full text-xs transition-colors ${leadFormId === f.id ? "bg-teal-600 text-white" : "bg-white/5 hover:bg-white/10"}`}
                    data-testid={`campaigns-leadform-${f.id}`}>
                    {f.name}
                  </button>
                ))}
              </div>
              {leadFormId && <p className="text-xs text-teal-400 mt-1">Leads from this form will be attributed to the campaign.</p>}
            </div>
          )}

          <div>
            <Label>Channels</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {CHANNELS.map((p) => (
                <button key={p.value} onClick={() => canGenerate && toggleChannel(p.value)} disabled={!canGenerate}
                  className={`px-4 py-1.5 rounded-full text-sm transition-colors ${channels.includes(p.value) ? "bg-violet-600 text-white" : "bg-white/5 hover:bg-white/10"}`}
                  data-testid={`campaigns-platform-${p.value}`}>
                  {p.label} {channels.includes(p.value) && "✓"}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Posts rotate through the campaign angles (problem → education → insight → proof → value → objection → CTA) across your selected channels.</p>
          </div>

          <Button onClick={generate} disabled={!canGenerate || generating || !selectedGoal} className="bg-violet-600 hover:bg-violet-700 rounded-full" data-testid="campaigns-generate-btn">
            {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
            {generating ? "Generating campaign... (~30 sec)" : "Generate Campaign"}
          </Button>
        </CardContent>
      </Card>

      {activeCampaign && (
        <Card className="glass border-violet-500/30" data-testid="campaigns-active-result">
          <CardHeader>
            <div className="flex items-baseline justify-between gap-2 flex-wrap">
              <CardTitle>"{activeCampaign.topic}"</CardTitle>
              <Badge>{activeCampaign.goal_label}</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {activeCampaign.affiliate_link && (
              <div className="p-3 rounded-lg bg-teal-600/10 border border-teal-600/30 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs text-teal-400 font-semibold uppercase tracking-wider">Tracked link auto-created (campaign-attributed)</p>
                  <p className="text-sm font-mono truncate">{BACKEND_URL}/api/r/{activeCampaign.affiliate_link.short_code}</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(`${BACKEND_URL}/api/r/${activeCampaign.affiliate_link.short_code}`); toast.success("Copied"); }} data-testid="campaigns-copy-link-btn"><Copy className="w-4 h-4" /></Button>
              </div>
            )}
            <div className="space-y-3">
              {(activeCampaign.pieces || []).map((p) => (
                <Card key={p.id} className="bg-white/5 border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <Badge variant="secondary">{p.label}</Badge>
                      {p.angle && <Badge className="bg-violet-600/15 text-violet-400 border-violet-600/25 text-[10px]">{p.angle}</Badge>}
                      {p.channel && <Badge className="bg-white/10 text-muted-foreground border-white/15 text-[10px]">{p.channel}</Badge>}
                      <span className="flex-1" />
                      <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(p.text); toast.success("Copied"); }}><Copy className="w-3 h-3" /></Button>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{p.text}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button size="sm" onClick={() => scheduleCampaignWeek(activeCampaign.id)} className="bg-teal-600 hover:bg-teal-700" data-testid="campaigns-schedule-week-btn">
                <CalendarDays className="w-4 h-4 mr-2" />Auto-schedule week (1 post/day from tomorrow 9:00)
              </Button>
              <p className="text-xs text-muted-foreground">X & LinkedIn pieces auto-publish · others get a share reminder · edit anything first in the Content Library</p>
            </div>
            <p className="text-xs text-muted-foreground">All pieces are drafts saved in your Content Library — review, edit and schedule them from there. Nothing publishes without your approval.</p>
          </CardContent>
        </Card>
      )}

      {campaigns.length > 0 && (
        <Card className="glass">
          <CardHeader><CardTitle>Campaign funnels</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {campaigns.map((c) => (
              <div key={c.id} className="p-3 rounded-lg bg-white/5" data-testid={`campaign-row-${c.id}`}>
                <button className="w-full flex items-center justify-between gap-3 text-left" onClick={() => setExpandedId(expandedId === c.id ? null : c.id)} data-testid={`campaign-expand-${c.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm truncate">{c.topic}</p>
                    <p className="text-xs text-muted-foreground">{c.goal_label} · {c.content_count} pieces · {new Date(c.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {c.affiliate_link && <Badge variant="secondary" className="text-xs">tracked</Badge>}
                    <span className="text-xs text-violet-400">{expandedId === c.id ? "Hide funnel ▲" : "View funnel ▼"}</span>
                  </div>
                </button>
                {expandedId === c.id && <CampaignFunnel campaignId={c.id} />}
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
